/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Carousel } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { PromoBannerCloseButton, PromoBannerLinks, PromoBannerText } from './components';
import { IDobbiesCtaSectionProps, IDobbiesCtaSectionResources } from './dobbies-cta-section.props.autogenerated';

export interface IPromoBannerState {
    animating: boolean;
    isVisible: boolean;
    activeIndex: number;
}

export interface IPromoBannerViewProps extends IDobbiesCtaSectionProps<{}>, IPromoBannerState {
    promoBannerProps: IModuleProps;
    contentProps: INodeProps;
    bannerMessages: any;
    promoBannerTextProps: INodeProps;
    promoBannerLinksProps: INodeProps;
    carouselProps: INodeProps;
    closeButton?: React.ReactNode;
    className: string;
    promoBannerText: string;
    resources: IDobbiesCtaSectionResources;
    onDismiss(): void;
    handleText(): void;
    handleLinks(): void;
}

/**
 *
 * PromoBannerModule component.
 * @extends {React.PureComponent<IDobbiesCtaSectionProps<IPromoBannerState>>}
 */
class PromoBanner extends React.PureComponent<IDobbiesCtaSectionProps<{}>, IPromoBannerState> {
    public state: IPromoBannerState = {
        animating: false,
        isVisible: true,
        activeIndex: 0
    };

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public render(): JSX.Element | null {
        const { dismissEnabled, interval, hideFlipper, autoplay, className = '' } = this.props.config;
        const { closeButtonAriaLabel, closeButtonLabel, bannerText } = this.props.resources;
        const { isVisible, activeIndex } = this.state;

        if (!isVisible) {
            this.props.context.telemetry.error('Promo banner content is empty, module wont render.');
            return null;
        }

        const viewProps = {
            // Context for telemetry and raw data for C1 to do customization
            // Including computed data, state data and methods
            ...this.props,
            className: classnames('ms-promo-banner', className),
            isVisible, // State data
            onDismiss: this._onDismiss, // Methods
            handleLinks: this.handleLinkTextChange,
            handleText: this.handleTextChange,

            promoBannerProps: { className: classnames('ms-promo-banner', className), moduleProps: this.props, role: 'banner' },
            promoBannerTextProps: {
                tag: PromoBannerText
            },
            promoBannerLinksProps: {
                tag: PromoBannerLinks,
                telemetryContent: this.telemetryContent
            },
            contentProps: { className: 'ms-promo-banner__content' },
            promoBannerText: bannerText,

            activeIndex,
            carouselProps: {
                tag: Carousel,
                className: 'ms-promo-banner__carousel',
                activeIndex,
                next: this._next,
                previous: this._previous,
                onIndicatorsClickHandler: this._goToIndex,
                hideIndicator: hideFlipper,
                interval,
                hideFlipper,
                autoplay
            },
            closeButton: dismissEnabled && (
                <PromoBannerCloseButton
                    {...{
                        onClose: this._onDismiss,
                        label: closeButtonLabel,
                        closeButtonAriaLabel,
                        telemetryContent: this.telemetryContent
                    }}
                />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleTextChange = (event: Msdyn365.ContentEditableEvent) => {};

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {};

    private readonly _next = (): void => {
        const nextIndex = this.state.activeIndex + 1;
        this._goToIndex(nextIndex);
    };

    private readonly _previous = (): void => {
        const nextIndex = this.state.activeIndex - 1;
        this._goToIndex(nextIndex);
    };

    private readonly _goToIndex = (index: number): void => {
        if (this.state.animating) {
            return;
        }
        this.setState({ activeIndex: index });
    };

    private readonly _onDismiss = (): void => {
        this.setState({ isVisible: false });
    };
}

export default PromoBanner;
